import { plugin, debug } from 'light-component';
import { useEffect, useState, type ReactElement, type Key } from 'react';
import { convert, TreeService, useTranslation } from '@common';
import * as Base from '@base';
import { Require } from '../component';
import styles from './style.module.less';
import type { AreaOptionItem, AreaSelectStrictProps, AreaSelectStrictValue } from './types';

const { SHOW_ALL } = Base.TreeSelect;

export function WidgetAreaSelectStrict(props: AreaSelectStrictProps): ReactElement {
  debug.widget('dashboard AreaSelectStrict', props);

  const [t] = useTranslation();

  const [areaOptions, setAreaOptions] = useState<AreaOptionItem[]>([]);
  const [treeData, setTreeData] = useState<Base.DataNode[]>([]);
  const [level, setLevel] = useState<number[]>([]);
  const [levelOptions, setLevelOptions] = useState<number[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const onChange = (value: AreaSelectStrictValue[]): void => {
    if (value == null || value.length === 0) {
      props.onChange?.(undefined);
      return;
    }
    props.onChange?.(value);
  };

  useEffect(() => {
    setLevelFun();
  }, [props.value]);

  const setLevelFun = (): void => {
    if (levelOptions.length === 0 || props.value == null) {
      return;
    }
    const levelValue: number[] = [];
    levelOptions.forEach(levelItem => {
      const currentLevelArea = areaOptions.filter(item => item.idPath.length === (levelItem + 1));
      if (currentLevelArea.every(area => props.value.some(item => item.value === area._id))) {
        levelValue.push(levelItem);
      }
    });
    setLevel(levelValue);
  };

  const onTreeExpand = (value: Key[]): void => {
    setExpandedKeys(value);
  };

  const onLevelChange = (value: number[]): void => {
    let check: number;
    if (value.length > level.length) {
      check = value.filter(item => !level.includes(item))?.[0];
    } else {
      check = level.filter(item => !value.includes(item))?.[0];
    }
    setLevel(value);

    const checkAreaValue = areaOptions.filter(item => item.idPath.length === (check + 1)).map(item => ({
      value: item._id,
      label: item.name,
      idPath: item.idPath
    }));

    const areaValue = props.value ?? [];
    let newAreaValue;

    if (value.length > level.length) {
      setExpandedKeys([...new Set([...expandedKeys, ...checkAreaValue.flatMap(item => item.idPath.slice(0, -1))])]);
      newAreaValue = Array.from(new Map([...areaValue, ...checkAreaValue].map(item => [item.value, item])).values());
    } else {
      newAreaValue = areaValue.filter(item => !checkAreaValue.some(check => check.value === item.value));
      if (newAreaValue.length === 0) {
        props.onChange?.(undefined);
        return;
      }
    }

    props.onChange?.(newAreaValue);
  };

  useEffect(() => {
    if (props.options == null) {
      return;
    }
    const option = props.options?.filter(item => item.groupType === 'area').map(item => ({
      ...item,
      icon: convert('AreaSvgIcon'),
      groupType: 'area' as const
    }));
    setAreaOptions(option);
    const treeData = new TreeService().toTree(option ?? []);
    setTreeData(treeData);

    const longest = option?.reduce((longest, item) => (item.idPath.length > longest ? item.idPath.length : longest), 0);
    const levelOptions: number[] = [];
    for (let index = 1; index < longest; index += 1) {
      levelOptions.push(index);
    }
    setLevelOptions(levelOptions);
    if (expandedKeys.length === 0) {
      setExpandedKeys([props.options?.find(item => item.parent === '')?._id ?? '']);
    }
    setLevelFun();
  }, [props.options]);

  return (
    <div className={props.className}>
      <div className={styles.input}>
        <Require require={props.require} />
        <span className={styles.title}>{t(props.title)}</span>
      </div>
      <Base.TreeSelect
        className={styles.treeSelect}
        treeIcon
        value={props.value}
        placeholder={t(props.placeholder ?? '')}
        showSearch
        allowClear
        autoClearSearchValue
        treeCheckStrictly
        size='middle'
        treeCheckable
        treeNodeFilterProp='title'
        treeExpandedKeys={expandedKeys}
        onTreeExpand={onTreeExpand}
        treeLine={{ showLeafIcon: false }}
        popupMatchSelectWidth={false}
        treeData={treeData}
        onChange={onChange}
        maxTagCount={1}
        showCheckedStrategy={SHOW_ALL}
        dropdownRender={menu => (
          <>
            <Base.Space direction='vertical' className={styles.areaLevelCheck}>
              <div>{t('通过下方选择框来选择同一层级的节点')}</div>
              <Base.Checkbox.Group options={levelOptions} onChange={onLevelChange} value={level} />
            </Base.Space>
            {menu}
          </>
        )}
      />
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'areaSelectStrict', component: WidgetAreaSelectStrict });
